import React from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import RmsFormTemplate from '../components/RmsFormTemplate';
import { useAuth } from '../../hooks/useAuth';
import useRmsFlowContext from '../hooks/useRmsFlowContext';
import useRmsPostFormInstance from '../hooks/useRmsPostFormInstance';
import { apiSaveFormDraft, apiSendSafetySignposting } from '../utils/rmsApi';
import { isHfpFormKey } from '../utils/referralStatus';
import { areInstanceAnswersLocked } from '../utils/submissionConfirmation';
import { isHarbourSencoPortalEnabled, isHarbourFamilyPortalEnabled } from '../constants/orgFeatures';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { productForRmsFormKey } from '../utils/rmsProduct';
import { findSafetyTrigger } from '../utils/safetySignposting';
import RmsSafetyInfoDialog from '../components/RmsSafetyInfoDialog';
import HarbourBeforeYouStart from '../components/harbour/HarbourBeforeYouStart';
import HarbourButton from '../components/harbour/HarbourButton';
import HarbourNotice from '../components/harbour/HarbourNotice';
import HarbourTextLink from '../components/harbour/HarbourTextLink';
import { countSchemaQuestions, estimatedMinutes } from '../utils/harbourForms';
import { withServerFormContext } from '../utils/serverFormContext';
import { referredForLine } from '../utils/familyPortal';

/**
 * RmsPostFormPage
 * ---------------
 * Purpose:
 *   Generic post-account form page that renders a schema-driven form by formKey
 *   with autosave, resume, and submit capabilities.
 *
 * Props:
 *   - formKey: The form schema key to load
 *   - afterSubmitRoute: Function returning the route to navigate to after submit
 *   - backToRoute: Function returning the route for the back/breadcrumb navigation
 *   - analyticsName: Optional name for analytics tracking
 *   - beforeNext: Callback invoked before advancing to next step; return { proceed: false } to block
 *   - onFormSubmit: Optional callback invoked with finalAnswers after form submit completes,
 *                   before navigation. Use for side-effects like participant nomination upserts.
 */
/**
 * The question ids to hide on this form: only the school's Request for
 * Referral on a Harbour pupil whose record holds the wizard's answers, and only
 * where the form's answer IS the wizard's (so an answer typed into an older
 * draft is still shown rather than submitted unseen).
 *
 * @param {object} args
 * @param {string} args.formKey
 * @param {string|null} args.userRole - 'school_user' | 'guardian'
 * @param {boolean} args.harbourPupil - patientRecord.harbour.pupil
 * @param {object|null} args.referralPathway - patientRecord.referralPathway
 * @param {object} args.answers - the form's current answers (prefills merged)
 * @returns {string[]|null}
 */
export function harbourWizardHiddenQuestionIds({ formKey, userRole, harbourPupil, referralPathway, answers }) {
  if (userRole !== 'school_user' || !harbourPupil) return null;
  // Reason for Referral: the wizard's "what is the referral for" choice
  // already answered main_reason — hide it when the (prefilled) answer still
  // matches the record, exactly as the Request for Referral ids below.
  if (formKey === 'reasonForReferral') {
    const referralFor = referralPathway && referralPathway.referralFor;
    const expected = { adhd: 'adhd_concern', asd: 'autism_concern', both: 'both_concern' }[referralFor];
    const answer = (answers || {}).main_reason;
    if (expected && (answer?.optionId ?? answer) === expected) return ['main_reason'];
    return null;
  }
  if (formKey !== 'requestForReferral') return null;
  const told = referralPathway && referralPathway.guardianToldToExpectEmail;
  const informantEmail = String((referralPathway && referralPathway.informant && referralPathway.informant.email) || '')
    .trim().toLowerCase();
  const a = answers || {};
  const ids = [];
  const toldAnswer = a.guardian_told_to_expect_email;
  if (told && (toldAnswer?.optionId ?? toldAnswer) === told) ids.push('guardian_told_to_expect_email');
  const nomination = a.guardian_nomination;
  if (
    informantEmail
    && nomination && typeof nomination === 'object'
    && String(nomination.guardian_first_name || '').trim()
    && String(nomination.guardian_email || '').trim().toLowerCase() === informantEmail
  ) {
    ids.push('guardian_nomination');
  }
  return ids.length ? ids : null;
}

/** Whether any single-choice question in the form offers a "don't know" answer. */
function schemaOffersDontKnow(schema) {
  const DONT_KNOW = /don[’']?t know|not sure/i;
  for (const section of (schema && schema.sections) || []) {
    for (const q of section.questions || []) {
      if ((q.options || []).some((o) => DONT_KNOW.test(String(o.label || '')))) return true;
    }
  }
  return false;
}

// How long "Preparing form" may show before the page says it could not load
// (TDI-688). Longer than the bundle call's own limit, so a stalled bundle
// reports through the hook first; this also catches a stalled record read or
// sign-in check, which have no limit of their own.
export const PREPARING_FORM_TIMEOUT_MS = 20000;

/**
 * The design's "could not load" state (frames 3307:2146 phone, 3474:145143
 * desktop, wording verbatim): an error notice, Try again (gold on the family
 * side, the secondary button for school staff, who never see gold) and Back.
 */
function FormLoadError({ audience, onRetry, backTo, accessDenied = false }) {
  return (
    <div className="harbour harbour-page">
      <main className="flex flex-col gap-4 max-w-[760px] px-4 py-8 mx-auto">
        {backTo && (
          <div>
            <HarbourTextLink to={backTo} leadingIcon="chevron-left" size="standard">Back</HarbourTextLink>
          </div>
        )}
        {accessDenied ? (
          // A refusal is not a connection problem, and trying again would only
          // be refused again: for example a teacher following an old link to a
          // form the SENCO has since kept or given to someone else.
          <HarbourNotice kind="error" title="This form is not one of yours">
            It may have been given to someone else, or the link may be out of date. Go back to see your forms.
          </HarbourNotice>
        ) : (
          <>
            <HarbourNotice kind="error" title="We could not load this page right now">
              Check your connection, then try again.
            </HarbourNotice>
            <div>
              <HarbourButton kind={audience === 'family' ? 'family' : 'secondary'} onClick={onRetry}>
                Try again
              </HarbourButton>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function isPermissionDenied(code) {
  return /(^|\/)permission-denied$/.test(String(code || ''));
}

/**
 * Each "Try again" mounts the page afresh, so every load it waits on (the
 * record, the sign-in check and the form bundle, asked for again rather than
 * served from cache) runs again from the start.
 */
export default function RmsPostFormPage(props) {
  const [attempt, setAttempt] = React.useState(0);
  const retry = React.useCallback(() => setAttempt((n) => n + 1), []);
  return <RmsPostFormPageContent key={attempt} {...props} isRetry={attempt > 0} onRetry={retry} />;
}

function RmsPostFormPageContent({
  formKey,
  afterSubmitRoute = (patientRecordId) => `/rms/patient/${encodeURIComponent(patientRecordId)}/overview`,
  backToRoute = (patientRecordId) => `/rms/patient/${encodeURIComponent(patientRecordId)}/overview`,
  analyticsName,
  beforeNext,
  onFormSubmit,
  isRetry = false,
  onRetry,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { patientRecordId } = useParams();
  const patientRecordIdVal = patientRecordId || '';
  const { claims } = useAuth();
  const orgId = claims?.rmsOrgId;

  const { formContext, subjectFirstName, subjectPronouns, ready, userRole, isTeacher } = useRmsFlowContext(orgId, patientRecordIdVal);
  // A teacher cannot read the record, so the questions name the child from
  // the pupil the teacher's forms page opened this form for.
  const childFirstName = subjectFirstName || (isTeacher ? location.state?.pupilFirstName : null) || null;

  // ── Harbour chrome (SEN & ND Access Portal, TDI-53) ───────────────────────
  // School users and guardians fill the SAME forms through the same engine,
  // dressed in the Harbour chrome, and navigate back to their own Harbour
  // forms homepage instead of the legacy overview — whenever the record is a
  // Harbour pupil (such records only exist where the portal is in use) or the
  // whole org has that side's portal switched on. Legacy referral records at
  // non-Harbour orgs are untouched.
  const harbourEligible = userRole === 'school_user' || userRole === 'guardian';
  const [harbourRecord, setHarbourRecord] = React.useState({
    checked: false, pupil: false, senActive: false, referralPathway: null,
    submissionConfirmed: null, consentWithdrawal: null, formProgress: null,
  });
  React.useEffect(() => {
    let cancelled = false;
    if (!harbourEligible || !orgId || !patientRecordIdVal) return undefined;
    if (isTeacher) {
      // Teachers have no client reads of the record (TDI-489); the server
      // decides what they may open, save and submit.
      setHarbourRecord((r) => ({ ...r, checked: true }));
      return undefined;
    }
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'rms_organisations', orgId, 'patientRecords', patientRecordIdVal));
        const data = snap.exists() ? snap.data() || {} : {};
        if (!cancelled) {
          setHarbourRecord({
            checked: true,
            pupil: Boolean((data.harbour || {}).pupil),
            senActive: (data.senPathway || {}).status === 'active',
            referralPathway: data.referralPathway || null,
            submissionConfirmed: data.submissionConfirmed || null,
            consentWithdrawal: data.consentWithdrawal || null,
            formProgress: data.formProgress || null,
            // What the per-pathway lock reads (TDI-651): the SEND pathway's
            // own confirmations, and the facts that decide which pathway a
            // form and a legacy confirmation belong to.
            lockFacts: {
              harbour: data.harbour || null,
              intakeConcern: data.intakeConcern || null,
              senPathway: data.senPathway || null,
              senSubmissionConfirmed: data.senSubmissionConfirmed || null,
              hfpReportReadyNotifiedAt: data.hfpReportReadyNotifiedAt || null,
              hfpInstrument: data.hfpInstrument || null,
              domains: data.domains || null,
            },
          });
        }
      } catch (_) {
        if (!cancelled) setHarbourRecord({ checked: true, pupil: false, senActive: false, referralPathway: null, submissionConfirmed: null, consentWithdrawal: null, formProgress: null });
      }
    })();
    return () => { cancelled = true; };
  }, [harbourEligible, isTeacher, orgId, patientRecordIdVal]);
  const harbourRoleFlagOn = userRole === 'guardian'
    ? isHarbourFamilyPortalEnabled(orgId)
    : isHarbourSencoPortalEnabled(orgId);
  // Class teachers only exist on the portal, so their forms always wear it.
  const harbourChromeActive =
    harbourEligible && (isTeacher || harbourRoleFlagOn || harbourRecord.pupil);
  // A teacher cannot read the record, so the pathway a form was given to them
  // for comes from their pupil page.
  const harbourSenActive = isTeacher
    ? location.state?.pathway === 'sen'
    : harbourRecord.senActive;
  // Hold the first paint for school users until the record check lands, so
  // the page never flashes the wrong chrome.
  const harbourCheckPending = harbourEligible && !harbourRecord.checked;
  // The pathway group this form belongs to on the Harbour surfaces: the HFP
  // forms live under the SEN pathway while it is active; everything else (and
  // the HFP during a referral-only journey) lives under the referral pathway.
  const harbourGroup = isHfpFormKey(formKey) && harbourSenActive ? 'sen' : 'referral';
  let harbourHomeRoute = `/rms/pupils/${encodeURIComponent(patientRecordIdVal)}/forms/${harbourGroup}`;
  if (userRole === 'guardian') {
    harbourHomeRoute = `/rms/family/child/${encodeURIComponent(patientRecordIdVal)}/forms/${harbourGroup}`;
  } else if (isTeacher) {
    // Back to the pupil's page in the teacher's list (TDI-620).
    harbourHomeRoute = `/rms/teacher/pupils/${encodeURIComponent(patientRecordIdVal)}`;
  }
  // Orientation before the first question (shown once per visit, for a form
  // not yet started): how many questions, autosave, it can be done in stages.
  const [introDismissed, setIntroDismissed] = React.useState(false);
  const {
    schema, instanceId, answers, setAnswer, initialStepId,
    serverSectionProgress, formStatus, prefillSources, confirmPrefills,
    loading, error, errorCode, onSaveStep, onComplete, serverContext, serverLocked,
  } = useRmsPostFormInstance({
    orgId, patientRecordId: patientRecordIdVal, formKey, formContext, enabled: ready, forceFresh: isRetry,
  });
  // A teacher cannot read the record, so who started the referral (which
  // decides the school sections a referral form shows), and the child's sex
  // and school phase, come from the server.
  const pageContext = React.useMemo(
    () => withServerFormContext(formContext, serverContext),
    [formContext, serverContext]
  );

  // Harbour pupils: the referral wizard already asked for the informant and
  // the expect-the-email confirmation, so the Request for Referral never asks
  // again (their prefilled answers are still confirmed and submitted).
  const hiddenQuestionIds = harbourWizardHiddenQuestionIds({
    formKey,
    userRole,
    harbourPupil: harbourRecord.pupil,
    referralPathway: harbourRecord.referralPathway,
    answers,
  });
  const hiddenCount = hiddenQuestionIds ? hiddenQuestionIds.length : 0;

  // A parent is told what the school referred their child for, never asked
  // (R-023, TDI-631): the type is always the school's choice.
  const referredForNotice = userRole === 'guardian' && formKey === 'reasonForReferral'
    ? referredForLine(harbourRecord.referralPathway && harbourRecord.referralPathway.referralFor)
    : null;

  // ── Safety signposting (RMS-314) ──────────────────────────────────────────
  // When someone answers a safety question in a way that indicates risk, the
  // signposting is emailed to them and shown on screen before they move on.
  // This runs at answer time rather than on submit, matching the assessment
  // platform: someone who discloses and then abandons the form has still been
  // given help, and the service has still been told.
  // Once per question per form visit: a reader who goes back and forward again
  // is not made to re-acknowledge the same information.
  const [safetyDialog, setSafetyDialog] = React.useState(null);
  const safetyShownRef = React.useRef(new Set());
  const safetyResolveRef = React.useRef(null);

  const runSafetyCheck = React.useCallback(async (currentStep, currentAnswers) => {
    const trigger = findSafetyTrigger(currentStep, currentAnswers);
    if (!trigger || safetyShownRef.current.has(trigger.questionId)) return;
    safetyShownRef.current.add(trigger.questionId);

    // Persist the answer before asking the server to act on it. The step
    // autosave only runs once navigation advances, which is after this check,
    // so without an explicit save the server cannot see what was just
    // answered — and the provider alert is built from the stored answer, never
    // from anything the client asserts.
    //
    // Deliberately not awaited: the safety information must appear the instant
    // someone discloses, not after a round-trip. The save and the send are
    // chained so their order still holds, and both are non-blocking.
    try {
      apiSaveFormDraft({
        orgId,
        patientRecordId: patientRecordIdVal,
        formKey,
        instanceId: instanceId || formKey,
        patch: currentAnswers || {},
        stepId: currentStep?.stepId || currentStep?.id || '__safety_sync__',
        context: pageContext,
      })
        .then(() => apiSendSafetySignposting({
          orgId,
          patientRecordId: patientRecordIdVal,
          formKey,
          instanceId: instanceId || formKey,
          questionId: trigger.questionId,
        }))
        ?.catch((e) => {
          // Non-blocking by design. The submit-time alert is the backstop: if
          // this path fails the service is still told when the form is
          // submitted, deduplicated by the same 24-hour key.
          // eslint-disable-next-line no-console
          console.warn('[RMS][Safety] signposting/alert failed (non-blocking)', e);
        });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[RMS][Safety] signposting threw (non-blocking)', e);
    }

    // Hold navigation until the reader acknowledges.
    await new Promise((resolve) => {
      safetyResolveRef.current = resolve;
      setSafetyDialog({ questionId: trigger.questionId });
    });
  }, [orgId, patientRecordIdVal, formKey, instanceId, pageContext]);

  const acknowledgeSafety = React.useCallback(() => {
    setSafetyDialog(null);
    const resolve = safetyResolveRef.current;
    safetyResolveRef.current = null;
    if (resolve) resolve();
  }, []);

  // Compose with any page-supplied beforeNext; the safety check runs first and
  // a page guard that blocks still blocks.
  const beforeNextWithSafety = React.useCallback(async (currentStep, currentAnswers) => {
    await runSafetyCheck(currentStep, currentAnswers);
    if (typeof beforeNext === 'function') return beforeNext(currentStep, currentAnswers);
    return { proceed: true };
  }, [runSafetyCheck, beforeNext]);

  const onSubmit = React.useCallback(async (finalAnswers) => {
    // Final defensive sync to ensure server has latest answers + context before submit
    try {
      // eslint-disable-next-line no-console
      console.log('[RMS][PostForm] Final sync before submit', { orgId, patientRecordId: patientRecordIdVal, formKey });
      const resolvedInstanceId = instanceId || formKey;
      await apiSaveFormDraft({
        orgId,
        patientRecordId: patientRecordIdVal,
        formKey,
        instanceId: resolvedInstanceId,
        patch: finalAnswers || answers || {},
        stepId: '__final_sync__',
        context: pageContext
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[RMS][PostForm] Final sync failed (non-blocking)', e);
    }
    try {
      await onComplete(finalAnswers);
      
      // Call onFormSubmit callback if provided (e.g., for participant nomination upserts)
      if (typeof onFormSubmit === 'function') {
        try {
          await onFormSubmit(finalAnswers);
        } catch (submitErr) {
          // eslint-disable-next-line no-console
          console.warn('[RMS][PostForm] onFormSubmit callback error (non-blocking)', submitErr);
          // Don't block navigation on callback failure - form is already submitted
        }
      }
      
      // Navigate to the overview/dashboard after form submission — or, under
      // the Harbour chrome, back to the school-forms homepage so the next
      // form in the sequence is right there.
      navigate(harbourChromeActive ? harbourHomeRoute : afterSubmitRoute(patientRecordIdVal));
      
      // Scroll to top of page after navigation so user sees the forms list from the top,
      // not scrolled to the bottom where they left off. Use setTimeout to ensure navigation
      // has completed before scrolling.
      setTimeout(() => {
        try {
          window.scrollTo({ top: 0, behavior: 'instant' });
        } catch (_) {
          window.scrollTo(0, 0);
        }
      }, 0);
    } catch (e) {
      // Surface server-side validation details if present
      // eslint-disable-next-line no-console
      console.error('[RMS][PostForm] Submit failed', e, e?.details);
      throw e;
    }
  }, [onComplete, navigate, afterSubmitRoute, patientRecordIdVal, orgId, formKey, pageContext, answers, instanceId, onFormSubmit, harbourChromeActive, harbourHomeRoute]);

  const onHome = React.useCallback(() => {
    navigate(isTeacher ? '/rms/teacher' : '/rms/dashboard');
  }, [navigate, isTeacher]);

  // "Preparing form" waits on the record, the sign-in check and the bundle.
  // If it is still showing after PREPARING_FORM_TIMEOUT_MS, one of them has
  // stalled: offer Try again rather than leave the parent on a spinner with
  // no way on but Back (TDI-688). Answers are already saved server-side.
  const preparing = loading || !ready || harbourCheckPending || !schema;
  const [preparingTimedOut, setPreparingTimedOut] = React.useState(false);
  React.useEffect(() => {
    if (!preparing) {
      // A load that lands after all shows the form.
      setPreparingTimedOut(false);
      return undefined;
    }
    if (error) return undefined;
    const timer = setTimeout(() => setPreparingTimedOut(true), PREPARING_FORM_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [preparing, error]);

  if (error || preparingTimedOut) {
    return (
      <FormLoadError
        audience={userRole === 'guardian' ? 'family' : 'school'}
        onRetry={onRetry}
        backTo={harbourEligible ? harbourHomeRoute : backToRoute(patientRecordIdVal)}
        accessDenied={Boolean(error) && isPermissionDenied(errorCode)}
      />
    );
  }
  if (preparing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-white to-secondary/15 flex items-center justify-center">
        <div className="text-gray-700">Loading… Preparing form</div>
      </div>
    );
  }

  // Decide initial UI mode for the form (questions vs review):
  // 1. Submitted forms should ALWAYS open in review mode to show completed answers
  //    This fixes the bug where revisiting a submitted form shows incomplete sections
  // 2. URL query param `mode=review` can also force review mode for deep-linking
  // 3. Default remains `questions` for draft forms in progress
  const searchParams = new URLSearchParams(location.search || '');
  const urlModeParam = searchParams.get('mode');
  // Confirmed answers are locked (TDI-415): the respondent's own
  // confirm-and-submit locks their role's answers, so the form always opens
  // on a read-only Review regardless of what the URL asks for. The server
  // refuses writes regardless; this keeps the UI honest about it.
  // A teacher cannot read the record, so their lock comes from the server:
  // the form bundle carries the verdict of the same rule that refuses the
  // save (TDI-754), so every way into the form agrees — not only the pupil
  // page's link, which passes location.state.
  // A confirmation locks the forms that were submitted, so a form added
  // later opens for answers; and each pathway's confirmation locks only its
  // own forms (TDI-651), so the SEND forms stay open after a confirmed
  // referral until the SEND pathway itself is confirmed.
  const answersLocked = (isTeacher && location.state?.locked === true)
    || serverLocked === true
    || areInstanceAnswersLocked(
    {
      ...(harbourRecord.lockFacts || {}),
      submissionConfirmed: harbourRecord.submissionConfirmed,
      // A withdrawn referral locks both respondents' answers too (TDI-474),
      // so the form opens read-only rather than inviting answers the server
      // will refuse.
      consentWithdrawal: harbourRecord.consentWithdrawal,
      referralPathway: harbourRecord.referralPathway,
      formProgress: {
        ...(harbourRecord.formProgress || {}),
        ...(formStatus === 'submitted' ? { [instanceId]: { status: 'submitted' } } : {}),
      },
    },
    userRole,
    instanceId,
  );
  const initialMode = answersLocked || formStatus === 'submitted' || urlModeParam === 'review'
    ? 'review'
    : 'questions';

  // HFP forms use the compact question layout (see .hfp-compact-form in
  // index.css) so all answer options fit on screen without scrolling.
  const isHfpForm = isHfpFormKey(formKey);

  // The orientation counts only the questions this form will actually ask.
  const schemaQuestionCount = countSchemaQuestions(schema, userRole, 'school', {
    subjectSex: pageContext.subjectSex,
    schoolPhase: pageContext.schoolPhase,
  });
  const introQuestionCount = schemaQuestionCount == null
    ? null
    : (Math.max(0, schemaQuestionCount - hiddenCount) || null);

  const formTemplate = (
    <RmsFormTemplate
      schema={schema}
      answers={answers}
      setAnswer={setAnswer}
      // Locked answers are never saved: the server refuses the write, and the
      // read-only review used to fire it on every visit.
      onSaveStep={answersLocked ? undefined : onSaveStep}
      onComplete={onSubmit}
      beforeNext={beforeNextWithSafety}
      initialStepId={initialStepId}
      initialMode={initialMode}
      formContext={pageContext}
      brandBackTo={harbourChromeActive ? harbourHomeRoute : backToRoute(patientRecordIdVal)}
      chrome={harbourChromeActive ? 'harbour' : 'default'}
      chromeAudience={userRole === 'guardian' ? 'family' : 'school'}
      subject={{ firstName: childFirstName, pronouns: subjectPronouns }}
      chromeTone={harbourGroup === 'sen' ? 'sen' : 'referral'}
      chromeBackLabel={userRole === 'guardian' ? 'Parent or carer forms' : (isTeacher ? 'Your forms' : 'All school forms')}
      serverSectionProgress={serverSectionProgress}
      // Prefill tracking: questions auto-filled from previous submissions
      // Progress calculation should exclude these until user confirms via Next
      prefillSources={prefillSources}
      onConfirmPrefills={confirmPrefills}
      hiddenQuestionIds={hiddenQuestionIds}
      introNotice={referredForNotice}
      showBrand={false}
      showHome
      onHome={onHome}
      analyticsContext={{
        patientRecordId: patientRecordIdVal,
        product: productForRmsFormKey(formKey),
      }}
      // Pass orgId and patientRecordId for NominationField status checks
      orgId={orgId}
      patientRecordId={patientRecordIdVal}
      // Indicates if the form was previously submitted (for unsaved changes detection)
      wasSubmitted={formStatus === 'submitted'}
      locked={answersLocked}
      {...(isTeacher ? {
        reviewBarNote: 'You can change these answers until your SENCO submits the school’s forms.',
        // The teacher never confirmed anything; their SENCO's confirm is what
        // locked the school's answers (TDI-754).
        lockedNotice: 'Your SENCO has submitted the school’s forms, so these answers can no longer be changed.',
      } : {})}
    />
  );

  const body = isHfpForm ? <div className="hfp-compact-form">{formTemplate}</div> : formTemplate;

  return (
    <>
      {body}
      {harbourChromeActive && (
        <HarbourBeforeYouStart
          open={
            !introDismissed &&
            initialMode === 'questions' &&
            // Answers the wizard supplied (hidden questions) do not mean the
            // form has been started.
            Object.keys(answers || {}).filter((qid) => !(hiddenQuestionIds || []).includes(qid)).length === 0
          }
          onStart={() => setIntroDismissed(true)}
          formTitle={schema?.title || ''}
          tone={harbourGroup === 'sen' ? 'sen' : 'referral'}
          role={userRole}
          isTeacher={Boolean(isTeacher)}
          offersDontKnow={schemaOffersDontKnow(schema)}
          questionCount={introQuestionCount}
          minutes={estimatedMinutes(schema, introQuestionCount)}
          childFirstName={isHfpFormKey(formKey) ? childFirstName : null}
        />
      )}
      <RmsSafetyInfoDialog
        isOpen={Boolean(safetyDialog)}
        onAcknowledge={acknowledgeSafety}
        childName={childFirstName}
        respondentRole={pageContext?.currentUser?.role}
      />
    </>
  );
}


